import { tn } from "../../i18n";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";
import React from "react";

import { PrimaryButton, SecondaryButton } from "../../Button";
import * as Forms from "../../Forms";
import { Modal } from "../../Modal";
import { StatusSelector } from "../../StatusSelector";
import type * as Types from "../../TaskBoard/types";
import type { MilestonePage } from "../types";

export interface CompleteMilestoneModalProps {
  isOpen: boolean;
  milestoneName: string;
  openTaskCount: number;
  closedStatuses: Types.Status[];
  onClose: () => void;
  onComplete: (resolution: MilestonePage.OpenTasksResolution) => Promise<boolean>;
}

interface FormValues extends Forms.FormValues {
  resolutionAction: MilestonePage.OpenTasksResolution["action"];
  statusId: string;
}

export function CompleteMilestoneModal(props: CompleteMilestoneModalProps) {
  const { t } = useTranslation();
  const defaultStatus = preferredClosedStatus(props.closedStatuses);
  const form = Forms.useForm<FormValues>({
    fields: {
      resolutionAction: "move_to_no_milestone",
      statusId: defaultStatus?.id ?? "",
    },
    submit: async () => {
      const resolution = buildResolution(form.values, props.closedStatuses);

      if (!resolution) {
        form.actions.addErrors({ form: i18n.t("Select a closed task status.") });
        return;
      }

      const completed = await props.onComplete(resolution);
      if (!completed) {
        form.actions.addErrors({
          form: i18n.t("The milestone could not be completed. Check the tasks and try again."),
        });
        return;
      }

      form.actions.reset();
      props.onClose();
    },
    cancel: props.onClose,
  });

  React.useEffect(() => {
    if (!props.isOpen) return;

    form.actions.reset();
    form.actions.setValue("statusId", preferredClosedStatus(props.closedStatuses)?.id ?? "");
  }, [props.isOpen, props.closedStatuses]);

  const selectedStatus =
    props.closedStatuses.find((status) => status.id === form.values.statusId) ?? defaultStatus ?? null;
  const changesStatus = form.values.resolutionAction === "set_status";
  const taskSummary = tn(
    "This milestone has 1 open task. Choose what happens to them before completing it.",
    "This milestone has {{count}} open tasks. Choose what happens to them before completing it.",
    props.openTaskCount,
  );

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={() => void form.actions.cancel()}
      title={t("Complete “{{name}}”?", { name: props.milestoneName })}
      size="small"
      testId="complete-milestone-modal"
    >
      <Forms.Form form={form} className="space-y-5" testId="complete-milestone-form">
        <p className="text-sm text-content-base">{taskSummary}</p>

        <div className="space-y-2">
          <Forms.RadioButtons
            field="resolutionAction"
            label={t("Open tasks")}
            options={[
              { value: "move_to_no_milestone", label: t("Move tasks to No milestone") },
              { value: "set_status", label: t("Change tasks to a closed status") },
            ]}
          />

          <p className="text-xs text-content-dimmed">
            {changesStatus
              ? t("The tasks stay in this milestone and use the selected status.")
              : t("The tasks stay open and remain visible on the project task board.")}
          </p>
        </div>

        {changesStatus && selectedStatus && (
          <div className="space-y-1.5">
            <div className="text-sm font-medium text-content-base">{t("Task status")}</div>
            <StatusSelector
              statusOptions={props.closedStatuses}
              status={selectedStatus}
              onChange={(status) => form.actions.setValue("statusId", status.id)}
              variant="form-field"
              testId="complete-milestone-task-status"
            />
          </div>
        )}

        <Forms.FormError className="mt-2" />

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <SecondaryButton type="button" onClick={() => void form.actions.cancel()} disabled={form.state !== "idle"}>
            {t("Keep milestone active")}
          </SecondaryButton>
          <PrimaryButton type="submit" loading={form.state === "submitting"} disabled={form.state !== "idle"}>
            {t("Complete milestone")}
          </PrimaryButton>
        </div>
      </Forms.Form>
    </Modal>
  );
}

function preferredClosedStatus(statuses: Types.Status[]) {
  return statuses.find((status) => status.color === "green") ?? statuses[0] ?? null;
}

function buildResolution(values: FormValues, statuses: Types.Status[]): MilestonePage.OpenTasksResolution | null {
  if (values.resolutionAction === "move_to_no_milestone") {
    return { action: "move_to_no_milestone" };
  }

  const status = statuses.find((candidate) => candidate.id === values.statusId);
  return status ? { action: "set_status", status } : null;
}
